import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchVendorSharedApps, type SharedApp } from '@/lib/appSharing';

interface VendorAppsTabProps {
  marketplaceId?: number;
  vendorId: number;
}

/**
 * Applications mises à disposition par la marketplace.
 * Le propriétaire partage une app ; elle apparaît ici pour le vendeur,
 * qui peut l'ouvrir et l'utiliser pour son propre compte.
 */
export default function VendorAppsTab({ marketplaceId, vendorId }: VendorAppsTabProps) {
  const [apps, setApps] = useState<SharedApp[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!marketplaceId) {
        setApps([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await fetchVendorSharedApps(marketplaceId, vendorId);
      if (!active) return;
      setApps(result);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [marketplaceId, vendorId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (apps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-apps-2-line text-2xl text-foreground-400"></i>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 mb-1">Aucune application disponible</h3>
        <p className="text-sm text-foreground-500 max-w-sm">
          La marketplace ne vous a pas encore partagé d&apos;application. Dès qu&apos;elle en met une à
          disposition de ses vendeurs, elle apparaîtra ici.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {apps.map((app) => (
        <div key={app.appvendeurId} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
              <i className={`${app.icon} text-xl`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground-900 truncate">{app.nom}</h3>
              <span className="text-[11px] text-foreground-400">
                {app.tous ? 'Disponible pour tous les vendeurs' : 'Accès accordé par la marketplace'}
              </span>
            </div>
          </div>
          {app.description && (
            <p className="text-xs text-foreground-500 leading-relaxed line-clamp-3 mb-3 flex-1">{app.description}</p>
          )}

          {(app.limiteVendeur > 0 || app.conditionPlus) && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-background-100 border border-background-200/70">
              {app.limiteVendeur > 0 && (
                <p className="text-[11px] text-foreground-700 flex items-center gap-1.5">
                  <i className="ri-speed-up-line text-primary-500"></i>
                  Limité à <strong>{app.limiteVendeur}</strong> par vendeur
                </p>
              )}
              {app.conditionPlus && (
                <p className="text-[11px] text-foreground-500 mt-1 flex items-start gap-1.5">
                  <i className="ri-information-line mt-0.5"></i>
                  <span>{app.conditionPlus}</span>
                </p>
              )}
            </div>
          )}

          <Link
            to={`/vendor-dashboard/app/${app.nompage}`}
            className="mt-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
          >
            <i className="ri-play-circle-line"></i>
            Ouvrir l&apos;application
          </Link>
        </div>
      ))}
    </div>
  );
}